import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import {
	Cassette,
	CASSETTE_INITIAL_VALUES,
} from "@/pages/catalogs/models/Cassette";
import { Stand } from "@/pages/configuration/models/Stand";
import { PartFields } from "@/pages/configuration/pages/parts/PartFields";
import {
	StandGrid,
	useStandsByPart,
} from "@/pages/configuration/pages/parts/StandGrid";
import { setStandsToPart } from "@/pages/configuration/repositories/StandRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { Section } from "@/shared/models/Section";
import { setSectionsToPart } from "@/shared/repositories/SectionRepository";
import { SectionField } from "@/shared/selectors/SectionField";
import { useSectionsByPart } from "@/shared/selectors/SectionsOfPartSelector";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CassetteChockSelector } from "../../../../../shared/selectors";
import { upsertCassette } from "../../../repositories/CassetteRepository";

type Props = {
	cassette: Cassette | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const CassetteDetails = ({ cassette, modal, onHide }: Props) => {
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);
	const [sectionsSelected, setSectionsSelected] = useState<Section[]>([]);

	const {
		control,
		reset,
		handleSubmit,
		watch,
		getValues,
		formState: { dirtyFields },
	} = useForm<Cassette>({
		defaultValues: CASSETTE_INITIAL_VALUES,
		mode: "onChange",
	});

	const { data: sectionsOfPart, isSuccess: sectionsIsSuccess } =
		useSectionsByPart({
			partId: cassette?.PartId ?? 0,
			onError: (error) => setError(error.message),
		});
	const sectionsJson = JSON.stringify(
		sectionsSelected.map((section: Section) => ({
			...section,
			PartId: cassette?.PartId,
			PartName: cassette?.PartName,
		})),
	);
	const oldSectionsJson = JSON.stringify(sectionsOfPart);

	const { data: standsOfPart, isSuccess: standsIsSuccess } = useStandsByPart({
		partId: cassette?.PartId!,
		onError: (error) => setError(error.message),
	});

	const standsJson = JSON.stringify(
		standsSelected.map((stand: Stand) => ({
			...stand,
			PartId: cassette?.PartId,
			PartName: cassette?.PartName,
		})),
	);
	const oldStandsJson = JSON.stringify(standsOfPart);

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) ||
		standsJson !== oldStandsJson ||
		sectionsJson !== oldSectionsJson ||
		modal == "create";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertCassette, {
		meta: { successMessage: `The cassette was ${modal}d successfully` },
		onSuccess: async (id: number) => {
			if (id) {
				const resp = await setStandsToPart(id, standsSelected);
				if (!resp.ok) setError(resp.message);
			}
			if (id) {
				const resp = await setSectionsToPart(id, sectionsSelected);
				if (!resp.ok) setError(resp.message);
			}

			await queryClient.invalidateQueries({ queryKey: ["cassettes"] });
			await queryClient.invalidateQueries({ queryKey: ["sectionsByPart", id] });
			await queryClient.invalidateQueries({ queryKey: ["standsByPart", id] });
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Cassette> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (cassette?.IsDuplicate) {
				upsertMutation.mutate({ ...data, PartId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	useEffect(() => {
		if (sectionsOfPart !== undefined) {
			setSectionsSelected(sectionsOfPart || []);
		}
	}, [sectionsIsSuccess, sectionsOfPart]);
	useEffect(() => {
		if (standsOfPart !== undefined) {
			setStandsSelected(standsOfPart || []);
		}
	}, [standsIsSuccess, standsOfPart]);

	useEffect(() => {
		if (modal !== "") {
			reset(cassette || CASSETTE_INITIAL_VALUES);
			setIsSubmitLoading(false);
			if (cassette?.IsDuplicate) {
				if (standsOfPart && standsIsSuccess) {
					setStandsSelected(standsOfPart);
				}
				if (sectionsOfPart && sectionsIsSuccess) {
					setSectionsSelected(sectionsOfPart);
				}
			} else {
				setStandsSelected(standsOfPart || []);
				setSectionsSelected(sectionsOfPart || []);
			}
		}
	}, [modal, cassette]);

	const validateCassettes = (value: number | null, otherFieldName: any) => {
		const otherValue = watch(otherFieldName);
		return (
			value !== otherValue ||
			value === null ||
			`Cannot be the same as ${
				otherFieldName === "RivCassetteChockId" ? "River" : "Avenue"
			} Cassette`
		);
	};

	return (
		<>
			{(modal === "create" || modal === "update") && (
				<HelmetDexteel
					title={`${
						cassette === null ? "New Cassette" : `${cassette?.PartName ?? ""}`
					} - Cassettes`}
				/>
			)}
			{cassette === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a cassette or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						<PartFields<Cassette>
							partTypeCode="T"
							control={control}
							disabled={modal === "delete"}
							locationSizeMd={6}
						/>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="RivCassetteChockId"
								control={control}
								rules={{
									validate: {
										crossValidate: (value) =>
											validateCassettes(value, "AveCassetteChockId"),
									},
									deps: ["AveCassetteChockId"],
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<CassetteChockSelector
										id="river-cassette-chock-selector"
										label="River Cassette Chock"
										disabled={modal === "delete"}
										onChange={onChange}
										fieldError={error}
										value={value}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="AveCassetteChockId"
								control={control}
								rules={{
									validate: {
										crossValidate: (value) =>
											validateCassettes(value, "RivCassetteChockId"),
									},
									deps: ["RivCassetteChockId"],
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<CassetteChockSelector
										id="avenue-cassette-chock-selector"
										label="Avenue Cassette Chock"
										disabled={modal === "delete"}
										onChange={onChange}
										fieldError={error}
										value={value}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<SectionField
								label="Sections"
								onSelectionChanged={setSectionsSelected}
								sectionsOfPart={sectionsSelected}
								disabled={modal === "delete"}
								partId={0}
							/>
						</Grid2>
						<StandGrid
							onSelectionChanged={setStandsSelected}
							disabled={modal === "delete"}
							standsOfPart={standsSelected}
						/>
						<Grid2 size={{ sm: 12, md: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
										disabled={modal === "delete"}
									/>
								)}
							/>
						</Grid2>
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
